import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
// frontend/src/components/AlarmProfiles/ProfileDetail.jsx
import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Chip,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Alert
} from '@mui/material';
import {
    Close as CloseIcon,
    Edit as EditIcon,
    Assignment as AssignIcon,
    Star as StarIcon,
    Speed as SpeedIcon,
    Thermostat as TempIcon,
    Compress as PressureIcon
} from '@mui/icons-material';
import { profileUtils } from '../../services/api';

const ProfileDetail = ({
    open,
    onClose,
    profile,
    onEdit,
    onAssign,
    hasPermission
}) => {
  const { t } = useTranslation();
    if (!profile) return null;

    const getMetricIcon = (metricType) => {
        switch (metricType) {
            case 'PSI': return <PressureIcon fontSize="small" />;
            case 'TEMP': return <TempIcon fontSize="small" />;
            case 'ACC': return <SpeedIcon fontSize="small" />;
            default: return null;
        }
    };

    const formatThreshold = (config) => {
        if (!config) return '—';

        const min = config.threshold_min;
        const max = config.threshold_max;

        let range = '';
        if (min != null && max != null) range = `${min}–${max}`;
        else if (min != null) range = `≥${min}`;
        else if (max != null) range = `≤${max}`;
        else range = '—';

        return range;
    };

    const getConfigsByAxle = () => {
        const configsByAxle = {};

        if (profile.applies_to_all_axles) {
            const psiConfig = profile.configurations?.find(c => c.metric_type === 'PSI' && c.axle_number === 1);
            const tempConfig = profile.configurations?.find(c => c.metric_type === 'TEMP' && c.axle_number === 1);

            configsByAxle['Todos'] = {
                psi: psiConfig,
                temp: tempConfig
            };
        } else {
            for (let i = 1; i <= profile.axle_count; i++) {
                const psiConfig = profile.configurations?.find(c => c.metric_type === 'PSI' && c.axle_number === i);
                const tempConfig = profile.configurations?.find(c => c.metric_type === 'TEMP' && c.axle_number === i);

                configsByAxle[t('hardcoded.components_alarmprofiles_profilecard.eje_i', { i: i })] = {
                    psi: psiConfig,
                    temp: tempConfig
                };
            }
        }

        return configsByAxle;
    };

    const configsByAxle = getConfigsByAxle();
    const tpmsSettings = profile.tpms_settings || profileUtils.getDefaultTPMSSettings();

    const describeChannels = (severity) => {
        const severitySettings = tpmsSettings.channels_by_severity?.[severity] || {};
        return ['portal', 'email', 'whatsapp', 'voice']
            .filter((channel) => severitySettings[channel])
            .map((channel) => channel === 'voice' && severitySettings.voice_policy
                ? t('hardcoded.components_alarmprofiles_profiledetail.vozvoice_policyseveritysetting', { voice_policy: severitySettings.voice_policy, severitySetting: severitySettings.voice_delay_minutes != null ? ` ${severitySettings.voice_delay_minutes}m` : '' })
                : channel)
            .join(' | ') || t('hardcoded.components_alarmprofiles_profiledetail.sin_canales');
    };

    const getConfigPreview = (config) => {
        if (!config) return null;
        return profileUtils.buildPreview(config.metric_type, config, tpmsSettings);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" noWrap>
                            {profile.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {profile.axle_count} {i18n.t('hardcoded.components_alarmprofiles_assignmentstable.ejes')}{profile.applies_to_all_axles ? t('hardcoded.components_alarmprofiles_assignmentstable.todos') : t('hardcoded.components_alarmprofiles_profilecard.por_eje')}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {hasPermission('manager') && !profile.is_template && (
                            <Button startIcon={<EditIcon />} variant="contained" onClick={onEdit}>
                                {i18n.t('common.edit')}</Button>
                        )}
                        <IconButton onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {profile.description && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight={700}>
                            {i18n.t('audit.table.description')}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {profile.description}
                        </Typography>
                    </Box>
                )}

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {profile.is_template && <Chip label={t('hardcoded.components_alarmprofiles_profilecard.plantilla')} color="secondary" size="small" />}
                    <Chip
                        label={profile.active ? t('common.active') : t('hardcoded.components_alarmprofiles_assignmentstable.inactivo')}
                        color={profile.active ? 'success' : 'default'}
                        size="small"
                        variant={profile.active ? 'filled' : 'outlined'}
                    />
                    {profile.metrics_configured?.map((m) => (
                        <Chip key={m} icon={getMetricIcon(m)} label={m} size="small" variant="outlined" />
                    ))}
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                        {t('hardcoded.components_alarmprofiles_profiledetail.politica_tpms')}</Typography>
                    <Alert severity="info" sx={{ mb: 1 }}>
                        <Typography variant="body2">
                            {t('hardcoded.components_alarmprofiles_profiledetail.los_canales_de_notificacion_portal')}<strong> Perfil &gt; Notificaciones</strong> o
                            <strong> Empresa &gt; Usuarios &gt; Editar usuario &gt; Notificaciones</strong>.
                        </Typography>
                    </Alert>
                    <Alert severity="warning" sx={{ mb: 1 }}>
                        <Typography variant="body2">
                            {i18n.t('hardcoded.components_alarmprofiles_profiledetail.dual')}{tpmsSettings.dual_yoyo?.enabled ? t('common.active') : t('hardcoded.components_alarmprofiles_assignmentstable.inactivo')} {t('hardcoded.components_alarmprofiles_profiledetail.metodo')}{tpmsSettings.dual_yoyo?.comparison_method} {i18n.t('hardcoded.components_alarmprofiles_profiledetail.dif')}{tpmsSettings.dual_yoyo?.allowed_difference_percent}/{tpmsSettings.dual_yoyo?.critical_difference_percent}/{tpmsSettings.dual_yoyo?.extreme_difference_percent}%
                        </Typography>
                    </Alert>
                    {(tpmsSettings.no_report_sensor?.enabled || tpmsSettings.no_report_receiver?.enabled) && (
                        <Alert severity="warning">
                            <Typography variant="body2">
                                {tpmsSettings.no_report_sensor?.enabled && (
                                    <>{t('hardcoded.components_alarmprofiles_profiledetail.sensor_sin_reporte')}{tpmsSettings.no_report_sensor?.value ?? 12} {tpmsSettings.no_report_sensor?.unit === 'days' ? t('hardcoded.components_alarmprofiles_profiledetail.dias') : t('hardcoded.components_alarmprofiles_profiledetail.horas')}</>
                                )}
                                {tpmsSettings.no_report_sensor?.enabled && tpmsSettings.no_report_receiver?.enabled && ' | '}
                                {tpmsSettings.no_report_receiver?.enabled && (
                                    <>{t('hardcoded.components_alarmprofiles_profiledetail.receptor_sin_reporte')}{tpmsSettings.no_report_receiver?.value ?? 12} {tpmsSettings.no_report_receiver?.unit === 'days' ? t('hardcoded.components_alarmprofiles_profiledetail.dias') : t('hardcoded.components_alarmprofiles_profiledetail.horas')}</>
                                )}
                            </Typography>
                        </Alert>
                    )}
                </Box>

                <Box>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                        {t('hardcoded.components_alarmprofiles_assignmentstable.configuraciones')}</Typography>

                    <Table size="small" sx={{ '& td, & th': { py: 0.75 } }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'red.100' }}>
                                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
                                    {i18n.t('hardcoded.components_alarmprofiles_profiledetail.ejes')}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
                                    {t('hardcoded.components_alarmprofiles_profilecard.presion')}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
                                    {i18n.t('hardcoded.components_alarmprofiles_profilecard.temperatura')}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
                                    {t('hardcoded.components_alarmprofiles_profiledetail.optimo')}</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {Object.entries(configsByAxle).map(([axleName, configs]) => (
                                <TableRow key={axleName}>
                                    <TableCell sx={{ fontWeight: 600 }}>{axleName}</TableCell>
                                    <TableCell>
                                        <Box>
                                            <Typography variant="body2">
                                                {formatThreshold(configs.psi)}
                                                {configs.psi && ` ${configs.psi.unit}`}
                                            </Typography>
                                            {configs.psi?.ideal_value && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                                    <StarIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                                                    <Typography variant="caption" color="text.secondary">
                                                        {i18n.t('hardcoded.components_alarmprofiles_profiledetail.ideal')}{configs.psi.ideal_value} {configs.psi.unit}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {getConfigPreview(configs.psi)?.close_zone && (
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                    {t('hardcoded.components_alarmprofiles_profiledetail.cierre_automatico_de_ticket')}{getConfigPreview(configs.psi).close_zone.min} a {getConfigPreview(configs.psi).close_zone.max} {configs.psi.unit}
                                                </Typography>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box>
                                            <Typography variant="body2">
                                                {formatThreshold(configs.temp)}
                                                {configs.temp && ` ${configs.temp.unit === 'C' || configs.temp.unit === 'F' ? '°' + configs.temp.unit : configs.temp.unit}`}
                                            </Typography>
                                            {getConfigPreview(configs.temp)?.thresholds && (
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                    {t('hardcoded.components_alarmprofiles_profiledetail.atencioncriticaextrema')}{getConfigPreview(configs.temp).thresholds.alarm}/{getConfigPreview(configs.temp).thresholds.critical}/{getConfigPreview(configs.temp).thresholds.extreme} {configs.temp.unit === 'C' || configs.temp.unit === 'F' ? '°' + configs.temp.unit : configs.temp.unit}
                                                </Typography>
                                            )}
                                            {getConfigPreview(configs.temp)?.safe_below != null && (
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                    {t('hardcoded.components_alarmprofiles_profiledetail.cierre_automatico_de_ticket_por')}{getConfigPreview(configs.temp).safe_below} {configs.temp.unit === 'C' || configs.temp.unit === 'F' ? '°' + configs.temp.unit : configs.temp.unit}
                                                </Typography>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {[
                                                configs.psi?.ideal_value != null ? `P: ${configs.psi.ideal_value} ${configs.psi.unit}` : null,
                                                configs.temp?.ideal_value != null ? `T: ${configs.temp.ideal_value} ${configs.temp.unit}` : null
                                            ].filter(Boolean).join(' | ')}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>{t('common.close')}</Button>
                {hasPermission('manager') && !profile.is_template && (
                    <Button
                        variant="contained"
                        startIcon={<AssignIcon />}
                        onClick={onAssign}
                        disabled={!profile.active}
                    >
                        {i18n.t('common.assign')}</Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ProfileDetail;